import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ItemCard from "@/components/ItemCard";
import MapView from "@/components/MapView";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Map as MapIcon, LayoutGrid } from "lucide-react";

const CATEGORIES = ["All", "Furniture", "Electronics", "Books/Media", "Kids' Stuff",
                    "Garden/Outdoor", "Clothes", "Kitchen", "Other"];
const STATUSES = [
  { value: "", label: "Any status" },
  { value: "available", label: "Available" },
  { value: "claimed", label: "Claimed" },
  { value: "gone", label: "Gone" },
];

export default function Browse() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [status, setStatus] = useState("");
  const [view, setView] = useState("split"); // split | list | map (mobile)
  const [userLoc, setUserLoc] = useState(null);
  const [radius, setRadius] = useState(0); // 0 = off

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 5000 }
    );
    // Mount-only geolocation request; setUserLoc is a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (cat && cat !== "All") params.category = cat;
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    if (radius && userLoc) {
      params.lat = userLoc[0]; params.lng = userLoc[1]; params.radius_km = radius;
    }
    try {
      const { data } = await api.get("/items", { params });
      setItems(data);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [cat, status, q, radius, userLoc]);

  // Re-fetch automatically when filters change (q is search-on-click, not in deps).
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, status, radius]);

  const mapCenter = useMemo(() => {
    if (userLoc) return userLoc;
    if (items.length) return [items[0].lat, items[0].lng];
    return [40.7128, -74.006];
  }, [items, userLoc]);

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-5">
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">What's on the curb</h1>
        <p className="text-[#6B7280] mt-2">Browse free items neighbours have left out. Pop by, pick it up, take it home.</p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchItems()}
              placeholder="Search by title or keyword…"
              className="pl-9 h-11 rounded-full bg-white border-[#E8E5F0]"
            />
          </div>
          <select
            data-testid="status-filter"
            value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-11 rounded-full bg-white border border-[#E8E5F0] px-4 text-sm">
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            data-testid="radius-filter"
            disabled={!userLoc}
            value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="h-11 rounded-full bg-white border border-[#E8E5F0] px-4 text-sm disabled:opacity-50">
            <option value={0}>Any distance</option>
            <option value={2}>Within 2 km</option>
            <option value={5}>Within 5 km</option>
            <option value={10}>Within 10 km</option>
            <option value={25}>Within 25 km</option>
          </select>
          <Button onClick={fetchItems} className="h-11 rounded-full bg-[#0F0F12] hover:bg-black text-white px-6"
                  data-testid="search-btn">Search</Button>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
              className={`shrink-0 px-4 h-9 rounded-full text-sm border transition-colors
                ${cat === c
                  ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                  : "bg-white text-[#0F0F12] border-[#E8E5F0] hover:border-[#7C3AED] hover:text-[#7C3AED]"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-[#6B7280]" data-testid="results-count">
            {loading ? "Loading…" : `${items.length} item${items.length !== 1 ? "s" : ""}`}
          </p>
          <div className="md:hidden inline-flex bg-white border border-[#E8E5F0] rounded-full p-1">
            <button onClick={() => setView("list")} data-testid="view-list"
              className={`px-3 h-8 rounded-full text-sm inline-flex items-center gap-1 ${view !== "map" ? "bg-[#0F0F12] text-white" : "text-[#0F0F12]"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> List
            </button>
            <button onClick={() => setView("map")} data-testid="view-map"
              className={`px-3 h-8 rounded-full text-sm inline-flex items-center gap-1 ${view === "map" ? "bg-[#0F0F12] text-white" : "text-[#0F0F12]"}`}>
              <MapIcon className="h-3.5 w-3.5" /> Map
            </button>
          </div>
        </div>
      </div>

      {/* Split feed: list + map */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className={`md:col-span-5 ${view === "map" ? "hidden md:block" : ""}`}>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {["s1", "s2", "s3", "s4"].map((k) => (
                  <div key={k} className="aspect-[4/3] bg-white rounded-2xl border border-[#E8E5F0] animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="bg-white border border-[#E8E5F0] rounded-2xl p-10 text-center text-[#6B7280]"
                   data-testid="empty-state">
                Nothing here yet. Be the first to <a className="underline text-[#7C3AED]" href="/post">post a curb item</a>.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {items.map((it) => <ItemCard key={it.id} item={it} />)}
              </div>
            )}
          </div>
          <div className={`md:col-span-7 ${view === "list" ? "hidden md:block" : ""}`}>
            <div className="md:sticky md:top-20">
              <MapView items={items} center={mapCenter} zoom={userLoc ? 13 : 11}
                className="h-[60vh] md:h-[calc(100vh-9rem)]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
